import { useTranslation } from "react-i18next";
import "./BarChart.scss";

export type BarData = {
  label: string;
  value: number;
  percentage: number;
  color: string;
};

type BarChartProps = {
  data: BarData[];
  width?: number;
  height?: number;
  maxBarHeight?: number;
  showValues?: boolean;
  locale?: string;
};

function darkenColor(hex: string, amount = 0.3) {
  let c = hex.replace("#", "");
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  const num = parseInt(c, 16);
  let r = Math.floor(((num >> 16) & 255) * (1 - amount));
  let g = Math.floor(((num >> 8) & 255) * (1 - amount));
  let b = Math.floor((num & 255) * (1 - amount));
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function roundedTopRect(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  return `
    M${x},${y + height}
    L${x},${y + radius}
    Q${x},${y} ${x + radius},${y}
    L${x + width - radius},${y}
    Q${x + width},${y} ${x + width},${y + radius}
    L${x + width},${y + height}
    Z
  `;
}

export default function BarChart({
  data,
  width = 270,
  height = 210,
  maxBarHeight = 120,
  showValues = true,
}: BarChartProps) {
  const { i18n } = useTranslation();
  if (!data || data.length === 0) return null;

  const formatNumber = (value: number): string => {
    const targetLocale =
      i18n.language === "pt" || i18n.language === "pt-BR" ? "pt-BR" : "en-US";
    return value.toLocaleString(targetLocale);
  };

  const maxValue = Math.max(...data.map((d) => d.value)) || 1;

  // Barra: largura e espaçamento
  const barWidth = 44;
  const barGap = 22;
  const chartLeft = 10;
  const chartBottom = 38;

  return (
    <div className="bc-root" style={{ width, height }}>
      <svg width={width} height={height}>
        <defs>
          {data.map((d, i) => (
            <linearGradient
              id={`bar-grad-${i}`}
              key={i}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0%" stopColor={d.color} />
              <stop offset="100%" stopColor={darkenColor(d.color, 0.5)} />
            </linearGradient>
          ))}
        </defs>
        {data.map((d, i) => {
          const x = chartLeft + i * (barWidth + barGap);
          const barHeight = (d.value / maxValue) * maxBarHeight;
          const y = height - chartBottom - barHeight;
          return (
            <g key={d.label}>
              <path
                d={roundedTopRect(x, y, barWidth, barHeight, 6)}
                fill={`url(#bar-grad-${i})`}
              />
              {showValues && (
                <text
                  x={x + barWidth / 2}
                  y={y - 25}
                  textAnchor="middle"
                  className="body-regular"
                  fill="#fff"
                  fontWeight={600}
                >
                  {formatNumber(d.value)}
                </text>
              )}
              <text
                x={x + barWidth / 2}
                y={y - 10}
                textAnchor="middle"
                className="body-small"
                fill={d.color}
                fontWeight={600}
              >
                {formatNumber(d.percentage)}%
              </text>
            </g>
          );
        })}
      </svg>
      <div className="bc-legend">
        {data.map((d) => (
          <span className="bc-legend-item" key={d.label}>
            <span className="bc-legend-dot" style={{ background: d.color }} />
            <span className="body-small">{d.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
